import * as React from 'react';
import { Panel } from '../Panel';
import { FullSummaryGraph } from '../Graphs/FullSummaryGraph';
import { CONSTANTS } from '../../../assets/UIConstants';
import '../../../assets/styles/FullSummary.css';
import DropDownIcon from '../svgs/DropDownIcon';
import { computed } from 'mobx';
import { IDataPoint } from '../dashboardPanels/AbstractPanel';
import { Scan } from '../../../data_layer/models/Scan';
import { ScanStore } from '../../../data_layer/stores/ScanStore';
import * as moment from 'moment';
import { Link } from 'react-router-dom';
import { FullSummaryLinks } from '../../pages/FullSummary';
import { NumericRange } from '../Graphs/AreaGraph';
import { Comparision } from '../../pages/Public';

interface State {
    mode: FullSummaryGraphMode;
    isDropDownOpen: boolean;
}

interface Props {
    scanStore: ScanStore;
    scan_id?: number;
}

enum FullSummaryGraphMode {
    Weekly = 0,
    AllTime = 1
}

const clickEvent: string = 'click';

export abstract class FullSummaryPanel extends React.Component<Props, State> {
    private windowListener?: number;
    protected abstract infoText: JSX.Element | string;
    protected abstract categoryTitle: string;
    protected yAxisRange?: NumericRange;
    protected goodValueComparisionValue: number | undefined = undefined;
    protected goodValueComparision: Comparision | undefined = undefined;
    protected showYAxis = true;

    constructor(props: Props) {
        super(props);
        this.state = {
            mode: FullSummaryGraphMode.Weekly,
            isDropDownOpen: false,
        };
    }

    private setGraphMode(mode: FullSummaryGraphMode): void {
        this.setState({ mode: mode });
    }

    private getActiveStyle(mode: FullSummaryGraphMode): React.CSSProperties {
        let out: React.CSSProperties = fullSummaryStyle.buttonStyle;
        if (this.state.mode === mode) {
            out = { ...out, ...fullSummaryStyle.activeButtonStyle };
        }
        return out;
    }

    @computed private get graphDataPoints(): IDataPoint[] {
        let source: Scan[] = this.props.scanStore.recentScans;

        if (this.state.mode === FullSummaryGraphMode.AllTime) {
            source = this.props.scanStore.sortedByDateAscending;
        }

        const dataPoints: IDataPoint[] = [];
        source.forEach((scan: Scan) => {
            const tmp: IDataPoint = {
                x: scan.created_at.valueOf(),
                y: this.yValueExtractor(scan),
            };
            dataPoints.push(tmp);
        });

        return dataPoints;
    }

    protected generateXLabel(value: number): string {
        if (this.state.mode === FullSummaryGraphMode.AllTime) {
            const date = moment(value);
            return date.format('MMM');
        }

        const date = moment(value);
        return date.format('MM/DD');
    }

    protected abstract yValueExtractor(scan: Scan): number;

    private addClickEventListener(): void {
        document.addEventListener(clickEvent, this.eventListener);
    }

    private removeClickEventListener(): void {
        document.removeEventListener(clickEvent, this.eventListener);
    }

    private openMenu(): void {
        this.addClickEventListener();
        this.setState({ isDropDownOpen: true });
    }

    private eventListener = (ev: MouseEvent): void => {
        if (this.state.isDropDownOpen) {
            this.windowListener = window.setTimeout(() => {
                this.setState({ isDropDownOpen: false });
                this.removeClickEventListener();
            }, 200);
        }
    };

    public componentWillUnmount() {
        this.removeClickEventListener();
        if (this.windowListener) {
            window.clearTimeout(this.windowListener);
        }
    }

    @computed private get deltaText(): string {
        let deltaValue: number = 0;
        if (this.props.scanStore.recentScans.length > 1) {
            const currentValue = this.yValueExtractor(this.props.scanStore.mostRecentScan);
            const previousPoint = this.props.scanStore.recentScans[this.props.scanStore.recentScans.length - 2];
            deltaValue = currentValue - this.yValueExtractor(previousPoint);
        }

        let deltaString = deltaValue.toFixed(1);
        if (deltaValue >= 0) {
            deltaString = `+${deltaString}`;
        }
        return `${deltaString} from last scan`;
    }

    public render(): JSX.Element {
        return (
            <Panel
                id={'full-summary-panel'}
                style={fullSummaryStyle.panelStyle}
            >
                <div style={fullSummaryStyle.container}>
                    <div style={fullSummaryStyle.panelInputsRowContainer}>
                        <div style={fullSummaryStyle.panelMenuContainer}>
                            <div
                                style={fullSummaryStyle.dropDownStyle}
                                onClick={() => this.openMenu()}
                            >
                                <DropDownIcon
                                    color={CONSTANTS.COLORS.DARKEST_GREY}
                                    width={20}
                                    height={20}
                                />
                                <span style={{ marginLeft: 20 }}>
                                    {this.categoryTitle}
                                </span>
                            </div>
                        </div>
                        {this.state.isDropDownOpen &&
                            <div style={fullSummaryStyle.titleDropDown}>
                                {FullSummaryLinks.map((link, index) => {
                                    let route = `/full-summary/${link.route}`;
                                    if (undefined != this.props.scan_id) {
                                        route = `/history/${this.props.scan_id}/full-summary/${link.route}`;
                                    }
                                    return (
                                        <Link
                                            key={index}
                                            to={route}
                                            style={fullSummaryStyle.selectStyle}
                                        >
                                            <div>
                                                {link.label}
                                            </div>
                                        </Link>
                                    );
                                }
                                )}
                            </div>
                        }
                    </div>

                    <div style={fullSummaryStyle.content}>
                        {this.infoText}
                    </div>

                    <div style={fullSummaryStyle.buttonContainer}>
                        <a
                            style={this.getActiveStyle(FullSummaryGraphMode.Weekly)}
                            href={'#'}
                            onClick={() => this.setGraphMode(FullSummaryGraphMode.Weekly)}
                        >
                            <div style={fullSummaryStyle.monthChangeText}>
                                {this.deltaText}
                            </div>
                            <div style={fullSummaryStyle.rangeLabelsText}>
                                <div>Last</div>
                                <div>Weeks</div>
                            </div>
                            <div style={fullSummaryStyle.rangeValueText}>
                                6
                            </div>
                        </a>
                        <a
                            style={this.getActiveStyle(FullSummaryGraphMode.AllTime)}
                            href={'#'}
                            onClick={() => this.setGraphMode(FullSummaryGraphMode.AllTime)}
                        >
                            <div style={fullSummaryStyle.allTimeLabelsText}>
                                <div>All</div>
                                <div>Time</div>
                            </div>
                            <div style={fullSummaryStyle.inMonthsText}>
                                In Months
                            </div>
                        </a>
                    </div>
                    <div style={fullSummaryStyle.graphContainer}>
                        <FullSummaryGraph
                            dataPoints={this.graphDataPoints}
                            xAxisTickFormatter={(value) => {
                                return this.generateXLabel(value);
                            }}
                            interval={this.state.mode === FullSummaryGraphMode.AllTime ? 'months' : 'weeks'}
                            yAxisRange={this.yAxisRange}
                            goodValueComparision={this.goodValueComparision}
                            goodValueComparisionValue={this.goodValueComparisionValue}
                            showYAxis={this.showYAxis}
                        />
                    </div>
                </div>
            </Panel>
        );
    }
}

const fullSummaryStyle: React.CSSProperties = {
    panelStyle: {
        maxWidth: 1200 + 'px',
    },
    graphContainer: {
        width: '100%',
        height: 400,
    },
    container: {
        display: CONSTANTS.DISPLAY.FLEX,
        flexGrow: CONSTANTS.FLEX_GROW.ONE,
        flexDirection: CONSTANTS.FLEX_DIRECTION.COLUMN,
        alignItems: CONSTANTS.ALIGN_ITEMS.CENTER,
        justifyContent: CONSTANTS.FLEX_JUSTIFY.CENTER,
        padding: '20px 40px 60px 40px',
    },
    title: {
        fontSize: 42,
        fontWeight: 600
    },
    content: {
        color: CONSTANTS.COLORS.THEME_GREY,
        fontSize: 20 + 'px',
        lineHeight: 35 + 'px'
    },
    buttonContainer: {
        display: CONSTANTS.DISPLAY.FLEX,
        flex: CONSTANTS.FLEX_GROW.ONE,
        flexBasis: '100',
        flexDirection: CONSTANTS.FLEX_DIRECTION.ROW,
        alignItems: CONSTANTS.ALIGN_ITEMS.CENTER,
        justifyContent: CONSTANTS.FLEX_JUSTIFY.CENTER,
    },
    buttonStyle: {
        display: CONSTANTS.DISPLAY.FLEX,
        alignItems: CONSTANTS.FLEX_ALIGN.END,
        justifyContent: CONSTANTS.FLEX_JUSTIFY.START,
        height: 85,
        textDecoration: 'none',
        color: CONSTANTS.COLORS.DARKEST_GREY,
        border: 2 + 'px solid ' + CONSTANTS.COLORS.GRAPH_GREY,
        boxSizing: 'border-box',
        width: 300,
        paddingBottom: 17
    },
    activeButtonStyle: {
        border: 0 + 'px',
        borderBottom: '4px solid ' + CONSTANTS.COLORS.THEME_BLUE,
        boxShadow: CONSTANTS.BOX_SHADOW.SUMMARY_BUTTON
    },
    dropDownStyle: {
        height: 30,
        cursor: 'pointer',
        marginLeft: 'auto',
        marginRight: 'auto',
        fontSize: 35,
        display: CONSTANTS.DISPLAY.FLEX,
        justifyContent: CONSTANTS.FLEX_JUSTIFY.CENTER,
        alignItems: CONSTANTS.ALIGN_ITEMS.CENTER
    },
    titleDropDown: {
        position: 'absolute',
        top: 10,
        width: '20%',
        textAlign: 'center',
        zIndex: 1000,
        backgroundColor: 'white',
        boxShadow: CONSTANTS.BOX_SHADOW.TABLE,
        marginLeft: '40%',
        marginRight: '40%'
    },
    selectStyle: {
        width: '100%',
        outline: 'none',
        height: 35,
        fontSize: 22,
        border: 'none',
        color: '#3D3F4A',
        textDecoration: 'none',
    },
    panelInputsRowContainer: {
        position: 'relative',
        width: '100%',
        marginTop: 20 + 'px'
    },
    panelMenuContainer: {
        display: 'flex',
        flexDirection: 'row'
    },
    monthChangeText: {
        fontSize: 17,
        color: CONSTANTS.COLORS.THEME_GREY,
        marginLeft: 20
    },
    rangeLabelsText: {
        fontSize: 20,
        marginLeft: 10,
        marginBottom: -2,
        textAlign: 'left'
    },
    rangeValueText: {
        fontSize: 60,
        marginBottom: -12,
        marginLeft: 10
    },
    allTimeLabelsText: {
        fontSize: 20,
        marginLeft: 30,
        textAlign: 'left',
        marginBottom: -1
    },
    inMonthsText: {
        fontSize: 17,
        color: CONSTANTS.COLORS.THEME_GREY,
        marginLeft: 20
    },
};


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/views/components/fullSummaryPanels/FullSummaryPanel.tsx